/* eslint frontbucket-patterns/no-new-sagas: "warn" */
import { call, put, select, take, delay, race } from 'redux-saga/effects';

import { ApiComment } from 'src/components/conversation-provider/types';
import { PullRequest, User } from 'src/components/types';
import {
  EXITED_CODE_REVIEW,
  CLEAR_IN_PROGRESS_QUEUE_TASK,
  updatePullRequest,
} from 'src/redux/pull-request/actions';
import {
  FETCH_PULL_REQUEST_QUEUE_STATUS,
  QUEUE,
  fetchPullRequestQueueStatus,
  setAsyncQueueInProgress,
} from 'src/redux/pull-request/queue-reducer';
import {
  getCurrentPullRequest,
  getInProgressQueueTask,
  getInlineComments,
  getCurrentPullRequestRoleForCurrentUser,
} from 'src/redux/pull-request/selectors';
import { getCurrentRepositoryUuid } from 'src/selectors/repository-selectors';
import { getCurrentUser } from 'src/selectors/user-selectors';
import { getWorkspacePlan } from 'src/selectors/workspace-selectors';

import { publishPullRequestEvent } from './utils/pull-request-event-analytics';

export const PULL_REQUEST_QUEUE_STATUS_DELAY = 3000;

export enum QUEUE_TASK_STATUSES {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

function* updatePullRequestStatus(json: PullRequest) {
  const currentPullRequest: PullRequest = yield select(getCurrentPullRequest);
  const currentUser: User = yield select(getCurrentUser);
  const updatedPullRequest = {
    ...currentPullRequest,
    updated_on: json.updated_on,
    queued: json.queued,
  } as PullRequest;

  yield put(
    updatePullRequest({
      type: QUEUE.SUCCESS,
      pullRequest: updatedPullRequest,
      currentUser,
    })
  );

  // publish analytics event
  const { isWorkspacePaid } = yield select(getWorkspacePlan);
  const repoUuid: string = yield select(getCurrentRepositoryUuid);
  const comments: ApiComment[] = yield select(getInlineComments);
  const role: string | null = yield select(
    getCurrentPullRequestRoleForCurrentUser
  );
  publishPullRequestEvent(
    'QUEUED',
    updatedPullRequest,
    comments,
    isWorkspacePaid,
    repoUuid,
    role
  );
}

export function* pollPullRequestQueueStatus(pollingLink: string) {
  while (true) {
    yield delay(PULL_REQUEST_QUEUE_STATUS_DELAY);
    yield put(fetchPullRequestQueueStatus(pollingLink));
    const { success, error } = yield race({
      success: take(FETCH_PULL_REQUEST_QUEUE_STATUS.SUCCESS),
      error: take(FETCH_PULL_REQUEST_QUEUE_STATUS.ERROR),
    });

    if (error) {
      throw Error(error.payload);
    }
    const { payload: json } = success;
    if (json.task_status === QUEUE_TASK_STATUSES.SUCCESS) {
      const { queue_result: pullRequest } = json;
      return pullRequest;
    }
  }
}

export function* startQueuePoll(pollingLink: string) {
  yield put(setAsyncQueueInProgress(true));

  try {
    // @ts-ignore
    return yield race({
      json: call(pollPullRequestQueueStatus, pollingLink),
      exitedCodeReview: take(EXITED_CODE_REVIEW),
    });
  } finally {
    // clear banner on initial queue request
    yield put(setAsyncQueueInProgress(false));

    // clear banner on in-progress queue
    yield put({
      type: CLEAR_IN_PROGRESS_QUEUE_TASK,
    });
  }
}

// poll queue task status on page refresh for in-progress queues
export function* pollQueueTaskStatusSaga() {
  // @ts-ignore
  const queueInProgress = yield select(getInProgressQueueTask);

  const pollingLink = queueInProgress?.href || queueInProgress;
  if (!pollingLink) {
    return;
  }

  try {
    const { json } = yield call(startQueuePoll, pollingLink);
    if (json) {
      yield call(updatePullRequestStatus, json);
    }
  } catch (e) {
    yield put({
      type: QUEUE.ERROR,
      payload: e.message,
      error: true,
    });
  }
}
